// Integer money: MC (1e-3 $ price), MQ (1e-3 contract qty), Cash (1e-6 $) so cash = mq * priceMc exactly.

export type MC = number;    // price, 1e-3 dollars, integer
export type MQ = number;    // quantity, 1e-3 contracts, integer
export type Cash = number;  // 1e-6 dollars, integer

export const MC_PER_DOLLAR = 1_000;
export const MQ_PER_CONTRACT = 1_000;
export const CASH_PER_DOLLAR = 1_000_000;

/** Full YES payout, in MC. A settled YES contract is worth $1. */
export const MC_SETTLE = 1_000;

/** Cash cost of `mq` contracts at `priceMc`. Exact by construction. */
export function cashOf(mq: MQ, priceMc: MC): Cash {
  return mq * priceMc;
}

/** Kalshi dollar strings ("0.4850") lose their last digit round-tripped through a double; parse without one. */
export function dollarsToCash(v: string | number | null | undefined): Cash | null {
  return decimalToScaled(v, 6);
}

/** Kalshi dollar string -> MC. `"0.4850"` -> 485. */
export function dollarsToMc(v: string | number | null | undefined): MC | null {
  return decimalToScaled(v, 3);
}

/** Kalshi fixed-point count -> MQ. `"3.00"` -> 3000. */
export function countToMq(v: string | number | null | undefined): MQ | null {
  return decimalToScaled(v, 3);
}

/** Exact decimal-string -> integer scaled by 10^places; digits past `places` round half-away-from-zero. */
export function decimalToScaled(
  v: string | number | null | undefined,
  places: number,
): number | null {
  if (v === null || v === undefined || v === '') return null;
  let s = typeof v === 'number' ? numToDecimalString(v) : String(v).trim();
  if (!s) return null;
  let sign = 1;
  if (s[0] === '+' || s[0] === '-') {
    if (s[0] === '-') sign = -1;
    s = s.slice(1);
  }
  if (!/^\d*(\.\d*)?$/.test(s) || s === '.') return null;
  const dot = s.indexOf('.');
  const intPart = dot < 0 ? s : s.slice(0, dot);
  let frac = dot < 0 ? '' : s.slice(dot + 1);
  let carry = 0;
  if (frac.length > places) {
    if (frac.charCodeAt(places) >= 53 /* '5' */) carry = 1;
    frac = frac.slice(0, places);
  }
  frac = frac.padEnd(places, '0');
  const n = Number((intPart || '0') + frac);
  if (!Number.isSafeInteger(n)) return null;
  return sign * (n + carry);
}

/** A JS number back to a plain decimal string (never exponent notation). */
function numToDecimalString(n: number): string {
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  // 12dp is well past any precision Kalshi quotes, and avoids "1e-7".
  return n.toFixed(12).replace(/0+$/, '').replace(/\.$/, '');
}

/** Cash -> an unsigned dollar string, e.g. `1234567` -> `"1.23"`. */
export function formatCash(c: Cash, places = 2): string {
  const neg = c < 0;
  const abs = Math.abs(c);
  const rounded = Math.round(abs / 10 ** (6 - places));
  const div = 10 ** places;
  const whole = Math.floor(rounded / div);
  const frac = String(rounded % div).padStart(places, '0');
  return `${neg ? '-' : ''}${whole.toLocaleString('en-US')}${places ? '.' + frac : ''}`;
}

/** Cash -> a signed display string with `$`, e.g. `"+$1.23"` / `"-$0.40"`. */
export function signedDollars(c: Cash, places = 2): string {
  return (c >= 0 ? '+$' : '-$') + formatCash(Math.abs(c), places);
}

/** MQ -> a contract count for display: `3000` -> `"3"`, `1500` -> `"1.5"`. */
export function formatMq(mq: MQ): string {
  if (mq % MQ_PER_CONTRACT === 0) return String(mq / MQ_PER_CONTRACT);
  return (mq / MQ_PER_CONTRACT).toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
}

/** MC -> the ladder's price label in cents. `485` -> `"48.5"`. */
export function formatMc(mc: MC, decimals = 1): string {
  return (mc / 10).toFixed(decimals);
}

/** Kalshi wants a 4-dp fixed-point dollar string for the YES leg. */
export function mcToDollarString(mc: MC): string {
  const neg = mc < 0;
  const tenThousandths = Math.abs(mc) * 10; // MC is 1e-3; a 4dp string is 1e-4
  const whole = Math.floor(tenThousandths / 10_000);
  const frac = String(tenThousandths % 10_000).padStart(4, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

/** Round `mq` down to whole contracts — never over-hedge a fractional fill. */
export function floorContracts(mq: MQ): MQ {
  return Math.floor(mq / MQ_PER_CONTRACT) * MQ_PER_CONTRACT;
}
